import type { ComponentType, ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslations } from "next-intl";
import {
  Activity,
  BarChart3,
  Bell,
  Briefcase,
  Building2,
  Handshake,
  ListChecks,
  LogOut,
  Settings,
  Users,
  Wallet,
} from "lucide-react";
import { cn, Logo, Tooltip } from "@chotca/ui";
import { useLogout } from "@chotca/api-client";
import { AppShellViewportLock } from "@chotca/app-shell";
import { setAccessToken } from "@/boot/access-token";
import { EmailVerifyBanner } from "./EmailVerifyBanner";

type NavDef = {
  href: string;
  labelKey: string;
  icon: ComponentType<{ size?: number; strokeWidth?: number }>;
};

const NAV: NavDef[] = [
  { href: "/dashboard", labelKey: "dashboard", icon: BarChart3 },
  { href: "/todo", labelKey: "todo", icon: ListChecks },
  { href: "/postings", labelKey: "postings", icon: Briefcase },
  { href: "/orgs", labelKey: "orgs", icon: Building2 },
  { href: "/accounts", labelKey: "accounts", icon: Users },
  { href: "/invites", labelKey: "invites", icon: Handshake },
  { href: "/finance", labelKey: "finance", icon: Wallet },
  { href: "/activity", labelKey: "activity", icon: Activity },
  { href: "/notifications", labelKey: "notifications", icon: Bell },
  { href: "/settings", labelKey: "settings", icon: Settings },
];

function navActive(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}

function RailNavLink({
  href,
  label,
  icon: Icon,
}: {
  href: string;
  label: string;
  icon: NavDef["icon"];
}) {
  const { pathname } = useLocation();
  const active = navActive(pathname, href);
  return (
    <Tooltip label={label} side="right">
      <Link
        to={href}
        aria-current={active ? "page" : undefined}
        aria-label={label}
        className={cn(
          "flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-all duration-200",
          active
            ? "bg-lime text-ink shadow-sm ring-2 ring-lime/35"
            : "text-ash hover:bg-mist hover:text-ink active:scale-95",
        )}
      >
        <Icon size={22} strokeWidth={1.5} />
      </Link>
    </Tooltip>
  );
}

function MobileNavLink({
  href,
  label,
  icon: Icon,
}: {
  href: string;
  label: string;
  icon: NavDef["icon"];
}) {
  const { pathname } = useLocation();
  const active = navActive(pathname, href);
  return (
    <Link
      to={href}
      className={cn(
        "flex min-w-0 flex-1 flex-col items-center gap-1 px-2 py-2 transition-colors",
        active ? "text-ink" : "text-ash hover:text-ink",
      )}
    >
      <span
        className={cn(
          "flex h-10 w-10 items-center justify-center rounded-full transition-colors",
          active ? "bg-lime/25 text-ink" : "bg-transparent",
        )}
      >
        <Icon size={22} strokeWidth={1.5} />
      </span>
      <span className="max-w-20 truncate text-center text-[10px] font-semibold leading-tight">
        {label}
      </span>
    </Link>
  );
}

export function AdminAppShell({
  brand,
  children,
}: {
  brand: string;
  children: ReactNode;
}) {
  const tNav = useTranslations("adminNav");
  const { pathname } = useLocation();
  const homeActive = navActive(pathname, "/todo");
  const logout = useLogout({
    redirectTo: "/login",
    onCleared: () => setAccessToken(null),
  });

  return (
    <div className="relative h-dvh overflow-hidden bg-mist md:h-auto md:min-h-screen md:overflow-visible">
      <AppShellViewportLock />
      <aside className="fixed left-4 top-1/2 z-40 hidden max-h-[min(640px,calc(100vh-2rem))] -translate-y-1/2 flex-col md:flex">
        <div className="flex flex-col items-center gap-3 rounded-[9999px] border border-smoke bg-paper px-2 py-4 shadow-[0_12px_48px_rgba(15,15,15,0.1)]">
          <Link
            to="/todo"
            aria-label="Admin"
            className={cn(
              "mb-1 flex h-11 w-11 items-center justify-center rounded-full transition-transform hover:scale-105 active:scale-95",
              homeActive && "ring-2 ring-smoke",
            )}
          >
            <Logo variant="square" size={36} />
          </Link>
          <div
            className="flex flex-col gap-2"
            role="navigation"
            aria-label="Main"
          >
            {NAV.map((n) => (
              <RailNavLink
                key={n.href}
                href={n.href}
                label={tNav(n.labelKey)}
                icon={n.icon}
              />
            ))}
          </div>
          <div className="mt-2 w-full border-t border-smoke pt-3">
            <Tooltip label="Đăng xuất" side="right">
              <button
                type="button"
                onClick={() => void logout()}
                aria-label="Đăng xuất"
                className="flex h-11 w-full items-center justify-center rounded-full text-ash transition-colors hover:bg-mist hover:text-ink"
              >
                <LogOut size={20} strokeWidth={1.5} />
              </button>
            </Tooltip>
          </div>
        </div>
      </aside>

      <div className="flex h-full flex-col overflow-y-auto overscroll-contain pb-19 [-webkit-overflow-scrolling:touch] md:h-auto md:min-h-screen md:overflow-visible md:overscroll-auto md:pb-0 md:pl-[calc(1rem+4rem+1.75rem)]">
        <header className="sticky top-0 z-30 border-b border-transparent bg-mist/90 px-4 py-4 backdrop-blur-md md:hidden">
          <div className="flex items-center gap-3">
            <Link
              to="/todo"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-paper shadow-sm ring-1 ring-smoke"
            >
              <Logo variant="square" size={28} />
            </Link>
            <div className="min-w-0">
              <p className="truncate text-title-md font-bold text-ink">
                {brand}
              </p>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 py-6 md:px-10 md:py-10">
          <div className="mx-auto w-full max-w-6xl space-y-6">
            <EmailVerifyBanner />
            {children}
          </div>
        </main>
      </div>

      <nav
        className="fixed inset-x-0 bottom-0 z-50 flex items-stretch justify-around border-t border-smoke bg-paper/95 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2 backdrop-blur-md md:hidden"
        aria-label="Main"
      >
        {NAV.slice(0, 5).map((n) => (
          <MobileNavLink
            key={n.href}
            href={n.href}
            label={tNav(n.labelKey)}
            icon={n.icon}
          />
        ))}
      </nav>
    </div>
  );
}
